import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { ensureSyntaxTree, foldAll } from "@codemirror/language";
import { oneDark } from "@codemirror/theme-one-dark";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getAuditBlob, type AuditRow } from "@/lib/api";
import { formatTimestamp } from "@/lib/utils";

interface AuditDetailModalProps {
  row: AuditRow | null;
  onClose: () => void;
}

function pretty(s: string | undefined): string {
  if (!s) return "";
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

export function AuditDetailModal({ row, onClose }: AuditDetailModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const blobQuery = useQuery({
    queryKey: ["audit-blob", row?.id],
    queryFn: () => (row?.blob_key ? getAuditBlob(row.id) : null),
    enabled: !!row?.blob_key,
  });

  if (!row) return null;
  const blob = blobQuery.data;
  const before = blob?.before ?? {};
  const after = blob?.after ?? {};
  const allKeys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).sort();

  let metadataParsed: unknown = null;
  if (row.metadata) {
    try { metadataParsed = JSON.parse(row.metadata); } catch { /* ignore */ }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => {
        if (e.currentTarget === e.target) onClose();
      }}
    >
      <div className="w-full max-w-6xl rounded-lg border border-border bg-card flex flex-col max-h-[92vh] overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Badge className="text-[10px]">{row.action}</Badge>
            <span className="text-sm font-medium truncate">{row.summary}</span>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">✕</button>
        </div>

        <div className="px-5 py-3 border-b border-border grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <Field label="When" value={formatTimestamp(row.ts)} />
          <Field label="Admin" value={`${row.admin_name} (${row.admin_discord})`} />
          <Field
            label="Target"
            value={
              row.target_playfab
                ? `${row.target_label ?? "—"} · ${row.target_playfab}`
                : "—"
            }
          />
          <Field
            label="Changed keys"
            value={row.changed_keys ?? "—"}
            mono
          />
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {!row.blob_key && !metadataParsed && (
            <p className="text-sm text-muted-foreground">No additional data captured for this entry.</p>
          )}

          {metadataParsed !== null && (
            <details className="rounded border border-border bg-muted/30">
              <summary className="px-3 py-2 cursor-pointer text-xs font-medium">Metadata</summary>
              <pre className="px-3 py-2 text-xs font-mono whitespace-pre-wrap break-all">
                {JSON.stringify(metadataParsed, null, 2)}
              </pre>
            </details>
          )}

          {row.blob_key && (
            <>
              {blobQuery.isLoading && <Skeleton className="h-32 w-full" />}
              {blobQuery.error && (
                <p className="text-sm text-destructive">Failed to load diff: {String(blobQuery.error)}</p>
              )}
              {blobQuery.data && allKeys.length === 0 && (
                <p className="text-sm text-muted-foreground">Blob present but empty.</p>
              )}
              {allKeys.map((k) => (
                <KeyDiff key={k} keyName={k} before={before[k]} after={after[k]} />
              ))}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground/80 text-[10px] uppercase tracking-wide">{label}</div>
      <div className={`truncate ${mono ? "font-mono text-[11px]" : ""}`} title={value}>{value}</div>
    </div>
  );
}

function KeyDiff({
  keyName,
  before,
  after,
}: {
  keyName: string;
  before: string | undefined;
  after: string | undefined;
}) {
  const beforeText = pretty(before);
  const afterText = pretty(after);
  const noChange = beforeText === afterText;

  return (
    <details className="rounded border border-border bg-muted/20" open={!noChange}>
      <summary className="px-3 py-2 cursor-pointer text-xs font-mono flex items-center justify-between">
        <span className="truncate">{keyName}</span>
        {noChange ? (
          <Badge variant="outline" className="text-[10px]">no change</Badge>
        ) : (
          <Badge variant="secondary" className="text-[10px]">changed</Badge>
        )}
      </summary>
      <div className="grid grid-cols-2 border-t border-border/40">
        <DiffPane title="Before" value={beforeText} />
        <DiffPane title="After" value={afterText} />
      </div>
    </details>
  );
}

function DiffPane({ title, value }: { title: string; value: string }) {
  return (
    <div className="border-r last:border-r-0 border-border/40">
      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border/40 bg-muted/30">
        {title}
      </div>
      {value ? (
        <CodeMirror
          value={value}
          readOnly
          theme={oneDark}
          extensions={[json()]}
          height="22rem"
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            highlightActiveLine: false,
            bracketMatching: true,
          }}
          onCreateEditor={(view) => {
            ensureSyntaxTree(view.state, view.state.doc.length, 5000);
            foldAll(view);
          }}
        />
      ) : (
        <div className="px-3 py-4 text-xs text-muted-foreground italic">empty</div>
      )}
    </div>
  );
}
